import * as React from 'react';
import Input from 'src/components/common/Input';
import EditOrDeleteCreditCardService from 'src/services/creditCard/EditOrDeleteCreditCardService';
import CreditCardInfoModel from 'src/model/creditCardInfo.model';
import './EditForm.scss';
import {
  addressUSA,
  addressCA
} from 'src/components/ShippingToChange/components/OneTimeAddress/validAddresses';
import Alert from 'src/components/common/Alert';
import Dropdown from 'src/components/common/Dropdown';
import { withI18n } from 'react-i18next';
import env from 'src/shared/env';

export interface IEditFormProps {
  onCancel: () => void;
  onSubmit: () => void;
  fields: CreditCardInfoModel;
  t?: any;
}

const EditForm: React.FC<IEditFormProps> = ({
  onCancel,
  onSubmit,
  fields,
  t
}) => {
  const [holderAddress1, setHolderAddress1] = React.useState(
    fields.holderAddress1
  );
  const [holderAddress2, setHolderAddress2] = React.useState(
    fields.holderAddress2
  );
  const [cardCity, setCardCity] = React.useState(fields.city);

  const [cardExpirationYear, setCardExpirationYear] = React.useState(
    Number(fields.expirationDate.substr(0, 4))
  );
  const [cardExpirationMonth, setCardExpirationMonth] = React.useState(
    Number(fields.expirationDate.substr(5, 2))
  );
  const [cardState, setCardState] = React.useState(
    fields.holderZipCode.substring(0, 2)
  );
  const [cardZip, setCardZip] = React.useState(
    fields.holderZipCode.substring(3)
  );

  const [errorMessage, setErrorMessage] = React.useState('');

  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

  const editCreditCard = () => {
    const month = String(cardExpirationMonth).padStart(2, '0');

    setIsProcessing(true);
    EditOrDeleteCreditCardService.updateCreditCardsInfo({
      cardHolderAddress1: holderAddress1,
      cardHolderAddress2: holderAddress2,
      cardToken: fields.cardToken,
      cardType: fields.cardType,
      city: cardCity,
      expirationDate: `${cardExpirationYear}-${month}`,
      holderStateZip: `${cardState} ${cardZip}`
    })
      .then(() => {
        onSubmit();
      })
      .catch(error => {
        setErrorMessage(error.response.data);
      })
      .finally(() => setIsProcessing(false));
  };

  const validateZip = (zip: string) => {
    if (env.REACT_APP_BASE_ENVIRONMENT === 'CA') {
      zip = zip.trim();
      zip = zip.substr(0, 7);
      if (zip.length === 0 || (zip.length < 4 && /^[a-z0-9]+$/i.test(zip))) {
        setCardZip(zip);
      } else if (zip.length >= 4 && /^[a-z 0-9]+$/i.test(zip)) {
        const rawZip = zip.replace(' ', '');
        const mainZip = rawZip.substring(0, 3);
        const subZip = rawZip.substring(3);
        setCardZip(`${mainZip} ${subZip}`);
      }
    } else {
      zip = zip.substr(0, 10);
      if (zip.length === 0 || (zip.length < 6 && /^[\d-]+$/.test(zip))) {
        setCardZip(zip);
      } else if (zip.length >= 6 && /^[\d-]+$/.test(zip)) {
        const rawZip = zip.replace('-', '');
        const mainZip = rawZip.substring(0, 5);
        const subZip = rawZip.substring(5);
        setCardZip(`${mainZip}-${subZip}`);
      }
    }
  };

  const handleExpirationYearChange = (selectedYear: string | number) => {
    setCardExpirationYear(Number(selectedYear));

    const date = new Date();
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();
    if (Number(selectedYear) === currentYear) {
      setCardExpirationMonth(
        Math.max(cardExpirationMonth, Math.min(currentMonth + 1, 12))
      );
    }
  };

  const isEditDisabled = () => {
    if (env.REACT_APP_BASE_ENVIRONMENT === 'CA') {
      if (cardZip.length === 7) {
        return false;
      }
    } else {
      if (cardZip.length === 5 || cardZip.length === 10) {
        return false;
      }
    }
    return true;
  };
  const cardAddresses =
    env.REACT_APP_BASE_ENVIRONMENT === 'CA' ? addressCA : addressUSA;

  return (
    <div className="rp-accountcard-container">
      {errorMessage && (
        <Alert
          type="error"
          styleClass="rp-accountcard-alert"
          message={errorMessage}
          icon="rpds-error"
          handleCloseAlert={() => setErrorMessage('')}
        />
      )}
      <div className="rp-accountcard--form-container">
        <div className="rp-accountcard-close">
          <i className="rpds-close close-modal" onClick={onCancel} />
        </div>
        <h1 className="rp-accountcard--form-title">
          {t('accountCreditCards.editCreditCardHeaderTitle')}
        </h1>
        <div className="container-fluid">
          <div className="rp-form-inputs-container row">
            <div className="col-sm-6">
              <div className="rp-edit--disabled">
                <span className="rp-edit--disabled__label">
                  {t('accountCreditCards.cardNumber')}
                </span>
                {`**** **** **** ${fields.cardToken.substring(12)}`}
              </div>
              <div className="rp-edit--disabled">
                <span className="rp-edit--disabled__label">
                  {t('accountCreditCards.cardType')}
                </span>
                {fields.cardType}
              </div>
              <div className="rp-input">
                <label>{t('accountCreditCards.expirationDate')}</label>
                <div className="rp-accountcard--form-select-container">
                  <Dropdown
                    onSelected={itemValue =>
                      setCardExpirationMonth(Number(itemValue))
                    }
                    options={Array(12)
                      .fill(null)
                      .map((value, index) => {
                        const monthOption = index + 1;
                        return {
                          value: monthOption,
                          label: String(monthOption).padStart(2, '0'),
                          disabled:
                            new Date().getFullYear() === cardExpirationYear &&
                            monthOption < new Date().getMonth() + 1
                        };
                      })}
                    activeOption={cardExpirationMonth}
                  />
                  <Dropdown
                    onSelected={handleExpirationYearChange}
                    options={Array(30)
                      .fill(null)
                      .map((value, index) => {
                        return {
                          value: index + new Date().getFullYear(),
                          label: (index + new Date().getFullYear()).toString()
                        };
                      })}
                    activeOption={cardExpirationYear}
                  />
                </div>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="rp-input rp-billing-address-input-container">
                <label>{t('accountCreditCards.tableBillingAddress')}</label>
                <Input
                  type="text"
                  value={holderAddress1}
                  onChange={e => setHolderAddress1(e)}
                />
                <Input
                  type="text"
                  value={holderAddress2}
                  onChange={e => setHolderAddress2(e)}
                />
                <div className="rp-input-bottom-space" />
              </div>
              <div className="rp-input">
                <label>{t('accountShippingAddress.city')}</label>
                <Input
                  type="text"
                  value={cardCity}
                  onChange={e => setCardCity(e)}
                />
                <div className="rp-input-bottom-space" />
              </div>
              <div className="rp-input">
                <label>
                  {env.REACT_APP_BASE_ENVIRONMENT === 'CA'
                    ? `${t('common.province')}/${t('common.postalCode')}`
                    : `${t('formData.state')}/${t('formData.zip')}`}
                </label>
                <Dropdown
                  options={cardAddresses.map(({ name, value }) => ({
                    label: name,
                    value
                  }))}
                  onSelected={selectOption => setCardState(selectOption)}
                  activeOption={cardState}
                />
                <Input
                  type="text"
                  value={cardZip}
                  onChange={e => validateZip(e)}
                  maxLength={env.REACT_APP_BASE_ENVIRONMENT === 'CA' ? 7 : 10}
                />
                <div className="rp-input-bottom-space" />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="rp-edit-form__button-list">
                <button
                  className="primary-button"
                  onClick={() => editCreditCard()}
                  disabled={isEditDisabled()}
                >
                  {isProcessing
                    ? `${t('common.processing')}`
                    : `${t('common.update')}`}
                </button>
                <button className="secondary-button" onClick={() => onCancel()}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withI18n()(EditForm);
